import AddIcon from '@material-ui/icons/Add';
import { getQuarter, subQuarters } from 'date-fns';
import { useContext, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Role } from '../../api/user/Role';
import { Venture } from '../../api/ventures/types/Venture';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { UserContext } from '../../contexts/user-context';
import { useVenturesContext } from '../../contexts/ventures-context';
import { getRoutePath, Pages } from '../../router/constants';
import { isMobile } from '../../utils/functions';
import TableActions from '../common/table-actions';

function VenturesRowActions({
  venture,
  variant,
}: {
  venture: Venture;
  variant?: 'table' | 'list';
}) {
  const history = useHistory();
  const location = useLocation();
  const { rb } = useResourceBundles();
  const { hasAccessToAction, hasRole } = useContext(UserContext);
  const {
    setMentorsAssignmentTargetVenture,
    setFoundersAssignmentTargetVenture,
    setProfileUpdateTargetVentureIds,
    setReportLinkTargetVentureIds,
    setArchiveTargetVentures,
    checkSchedulingWizardForMentor,
  } = useVenturesContext();

  const prevPath = `${location.pathname}${location.search}`;

  const isParticipant = useMemo(
    () =>
      (!hasRole(Role.Mentor) && !hasRole(Role.Founder)) ||
      venture.userIsAssigned,
    [venture, hasRole],
  );

  const lastQuarter = getQuarter(subQuarters(new Date(), 1));

  return (
    <TableActions
      variant={variant}
      disabled={!isParticipant}
      tooltip={
        !isParticipant ? 'You are not assigned to this venture' : undefined
      }
      actions={[
        {
          key: 'row-action-new-session',
          onClick: () =>
            history.push({
              pathname: getRoutePath(Pages.NEW_SESSIONS),
              state: {
                prevPath,
                ventureId: venture.id,
              },
            }),
          icon: <AddIcon fontSize='small' color='inherit' />,
          label: 'Session',
          hidden: !hasAccessToAction('session.create'),
        },
        {
          key: 'row-action-note',
          onClick: () =>
            history.push({
              pathname: getRoutePath(Pages.VENTURE_NOTES, {
                ventureId: venture.id,
              }),
              state: { prevPath },
            }),
          icon: <AddIcon fontSize='small' color='inherit' />,
          label: 'Note',
          hidden: !hasAccessToAction('venture.notes.view'),
        },
        {
          key: 'row-action-scheduling-wizard',
          onClick: () => checkSchedulingWizardForMentor(venture),
          label: 'Scheduling Wizard',
          hidden: !hasAccessToAction('venture.wizard'),
        },
        {
          key: 'row-action-assign-mentors',
          onClick: () => setMentorsAssignmentTargetVenture(venture),
          label: `Assign ${rb('mentors-u')}`,
          hidden: !hasAccessToAction('venture.details.update'),
        },
        {
          key: 'row-action-assign-founders',
          onClick: () => setFoundersAssignmentTargetVenture(venture),
          label: 'Assign Founders',
          hidden: !hasAccessToAction('venture.details.update'),
        },
        {
          key: 'row-action-enrich',
          onClick: () =>
            history.push({
              pathname: getRoutePath(Pages.VENTURE_DETAILS, {
                ventureId: venture.id,
              }),
              state: { prevPath, openEnrichModal: true },
            }),
          label: 'Enrich Profile',
          hidden: !hasAccessToAction('venture.updateProfile'),
        },
        {
          key: 'row-action-request-profile-update',
          onClick: () => setProfileUpdateTargetVentureIds([venture.id]),
          label: 'Request Profile Update',
          hidden: !hasAccessToAction('venture.updateProfile') || isMobile(),
        },
        {
          key: 'row-action-request-quarterly-report',
          onClick: () => setReportLinkTargetVentureIds([venture.id]),
          label: 'Request Quarterly Report',
          hidden: !hasAccessToAction('venture.updateProfile') || isMobile(),
        },
        {
          key: 'row-action-submit-quarterly-report',
          onClick: () =>
            history.push({
              pathname: getRoutePath(Pages.VENTURE_QUARTERLY_REPORT_FORM, {
                ventureId: venture.id,
              }),
              state: { prevPath },
            }),
          icon: <AddIcon fontSize='small' color='inherit' />,
          label: `Q${lastQuarter} Report`,
          hidden:
            !hasAccessToAction('venture.details.quarterReport') ||
            (isMobile() && !hasRole(Role.Founder)),
        },
        {
          key: 'row-action-archive',
          onClick: () => setArchiveTargetVentures([venture]),
          label: 'Archive',
          hidden:
            !hasAccessToAction('venture.details.update') ||
            venture.status === 'Archived',
        },
      ]}
    />
  );
}

export default VenturesRowActions;
